<script>
// @ts-check

import RouteSelect from '@/components/fields/RouteSelect.vue'
import { inject } from 'vue'

export default {
  name: 'BaseInnerNavigationView',
  components: {
    RouteSelect
  },
  props: {
    routes: {
      /** @type {import('vue').PropType<import('vue-router').RouteRecordRaw[]>} */
      type: Array,
      required: true
    },
    // Page title
    title: {
      type: String,
      required: true
    },
    // Disable default view template
    disableTemplate: {
      type: Boolean
    },
    disableMobileNavigation: {
      type: Boolean
    },
    hiddenRoutes: {
      /** @type {import('vue').PropType<string[]>} */
      type: Array,
      default: () => []
    },
    disabledRoutes: {
      /** @type {import('vue').PropType<string[]>} */
      type: Array,
      default: () => []
    }
  },
  setup() {
    return {
      isMobileAppAndIos: inject('isMobileAppAndIos', false)
    }
  },
  data: () => ({
    isNaviVisibleOnMobile: false
  }),
  computed: {
    currentRouteTitle() {
      return this.getRouteTitle(this.$route)
    }
  },
  methods: {
    /**
     * @param {import('vue-router').RouteConfig|import('vue-router').Route} route
     */
    getRouteTitle(route) {
      // Subscription details navigation title for ios mobile app
      if (this.isMobileAppAndIos && route.meta?.mobileTitle) {
        return this.$t(`routes.${route.meta.mobileTitle}`)
      }

      return this.$t(`routes.${route.name}`)
    },
    /**
     * @param {import('vue-router').RouteConfig} route
     */
    showSubroutes(route) {
      if (route.meta?.hideSubroutes) {
        return false
      }

      if (this.$route.name === route.name) {
        return true
      }

      if (
        route.children?.find(
          (childRoute) => childRoute.name === this.$route.name
        )
      ) {
        return true
      }

      return false
    }
  }
}
</script>

<template>
  <div class="-mx-4 flex flex-col sm:mx-0 md:flex-row md:gap-x-4">
    <div
      class="flex-col pt-4 pb-8 sm:pt-8 md:flex md:w-64 md:min-w-64"
      :class="{ 'hidden ': !isNaviVisibleOnMobile || disableMobileNavigation }"
    >
      <div class="hidden md:block md:pb-8">
        <span class="heading-2 border-b-2 border-primary pb-1 text-primary">
          {{ title }}
        </span>
      </div>

      <VList color="primary" density="compact">
        <template v-for="route in routes" :key="route.name">
          <VListItem
            :to="{ name: route.name }"
            :disabled="disabledRoutes.includes(route.name)"
            :title="getRouteTitle(route)"
            :class="{ strict: route.name === $route.name }"
          />

          <template v-if="showSubroutes(route)">
            <VListItem
              v-for="childRoute in route.children ?? []"
              :key="childRoute.name"
              :to="{ name: childRoute.name }"
              class="pl-8!"
              :title="getRouteTitle(childRoute)"
              :disabled="disabledRoutes.includes(childRoute.name)"
              :class="{
                strict: childRoute.name === $route.name
              }"
            />
          </template>
        </template>
      </VList>
    </div>
    <!-- view -->
    <div class="w-full">
      <RouterView v-if="disableTemplate" />
      <template v-else>
        <RouteSelect class="mx-4 md:hidden" :disabled-routes :hidden-routes />
        <div class="mt-4 border border-secondary-200 bg-white shadow-card">
          <h2
            class="heading-2 flex h-16 items-center px-4 text-primary md:h-20 md:px-12"
          >
            {{ currentRouteTitle }}
          </h2>

          <hr class="mb-4 text-secondary-200" />

          <div class="p-4 sm:px-8 md:px-12 md:py-6">
            <RouterView />
          </div>
        </div>
      </template>

      <component
        :is="$route.meta.extra"
        v-if="$route.meta?.extra"
        :key="$route.meta.extra.name"
        class="mx-4 mt-section sm:mx-0"
        v-bind="$route.meta.extraProps ?? {}"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@reference "../styles/main.css";

:deep(.v-list-item--active) {
  .v-list-item-title {
    @apply font-medium!;
  }

  &:not(.strict) {
    .v-list-item__overlay {
      @apply bg-white;
    }
  }
}
</style>
